import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@capacit-com/lara-law-shared-ui";
import { Info } from "lucide-react";
import { TimelineItem } from "@/models/legislation/Timeline";
import { resolveTimelineLabel } from "@/lib/consolidationViewerUtils";
import {
  resolveTimelineFamilyRoot,
  TIMELINE_SCOPE_PARAM,
  TimelineScope,
} from "@/lib/timelineUniverseParams";

type TimelineScopeSwitcherProps = {
  timelineScope: TimelineScope;
  timelineRootAccession: string;
  documentId: string;
  parentTimelineItems?: TimelineItem[];
  ownTimelineItems?: TimelineItem[];
};

const TimelineScopeSwitcher: React.FC<TimelineScopeSwitcherProps> = ({
  timelineScope,
  timelineRootAccession,
  documentId,
  parentTimelineItems,
  ownTimelineItems,
}) => {
  const [, setSearchParams] = useSearchParams();

  const parentFamilyRoot =
    resolveTimelineFamilyRoot(parentTimelineItems) ?? timelineRootAccession;
  const parentLabel = resolveTimelineLabel(
    parentFamilyRoot,
    parentTimelineItems
  );
  const ownLabel = resolveTimelineLabel(documentId, ownTimelineItems);

  const handleScopeChange = useCallback(
    (value: string) => {
      if (value !== "parent" && value !== "own") return;
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === "own") {
            next.set(TIMELINE_SCOPE_PARAM, "own");
          } else {
            next.delete(TIMELINE_SCOPE_PARAM);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border/60 bg-muted/20 px-4 sm:px-6 py-2">
      {timelineScope === "parent" ? (
        <p className="flex min-w-0 items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            Du ser tidslinjen for{" "}
            <span className="font-medium text-foreground">{parentLabel}</span>.
            Dette dokument har sin egen tidslinje.
          </span>
        </p>
      ) : (
        <p className="flex min-w-0 items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          <span>
            Du ser tidslinjen for{" "}
            <span className="font-medium text-foreground">{ownLabel}</span>.
          </span>
        </p>
      )}

      <ToggleGroup
        type="single"
        value={timelineScope}
        onValueChange={handleScopeChange}
        variant="outline"
        size="sm"
        spacing={0}
        className="ml-auto shrink-0"
        aria-label="Vælg tidslinjekontekst"
      >
        <ToggleGroupItem value="parent" aria-label={`Vis tidslinje for ${parentLabel}`}>
          {parentLabel}
        </ToggleGroupItem>
        <ToggleGroupItem value="own" aria-label={`Vis tidslinje for ${ownLabel}`}>
          {ownLabel}
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};

export default TimelineScopeSwitcher;
